import { makeAutoObservable } from "mobx";
import {
  AccessibleShareholderDetail,
  AddressInfo,
  ExtendedShareholderDTO,
  formatName,
  Gender,
  getGenderLabel,
  PersonInfo,
  ShareholderDetailsResponseDTO,
  UpdateShareholderDTO,
} from "../../types/Shareholder";
import { PhoneInputModel } from "../../components-v2/PhoneInput";
import { emptyPersonInfo } from "../../stores/ShareholdersStore";
import { lookupNorwegianCity } from "../../utils/postalCodes";
import { getAllCountriesSortedByName, norway } from "../../utils/countries";
import { idnr } from "@navikt/fnrvalidator";
import { isValidSwedishNationalId } from "./ShareholderModel";
import { removeAllSpaces, unblank } from "../../utils/strings";
import { utlNumberValidator } from "../../utils/other";
import { Language } from "../../i18n/languages";

import { TMessage } from "../../i18n/tt";
import {
  BooleanFieldModel,
  createEmailField,
  LocalDateFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "../../common/commonForms";
import { compact } from "lodash";
import { ApiService } from "../../api";
import AlertMessageStore from "../../stores/AlertMessageStore";
import { failure, ok, pending, Portfolio, Result } from "../../common/common";
import { DropdownMenuProps } from "../../components-v2/Dropdown";
import { SuggestedCompany } from "../../types/CompanyInfo";
import { Actor } from "../../types/SelectActors";
import { toShareholderReference } from "../../utils/UrnReference";
import { TabsProps } from "../../components-v2/Tabs";
import { PortfolioCompany } from "../../stores/PortfolioStore";
import {
  CompanySelectorContextOption,
  CompanySelectorContextType,
  prepCompanySelectorContextOptions,
} from "./portfolioCompanySelectorDomain";

export enum EditSingleShareholderTabs {
  ALL_FIELD = "ALL_FIELDS",
  NOT_SET = "NOT_SET",
  SELECT_COMPANIES = "SELECT_COMPANIES",
}

export type EditSingleShareholderParams = {
  apiService: ApiService;
  messageStore: AlertMessageStore;
  orgNumber: string;
  onSuccess: (shareholder?: ExtendedShareholderDTO) => void;
  onClose: () => void;
  locale: Language;
  shareholderId?: number;
  isShareholderView?: boolean;
  loadShareholders: () => Promise<Actor[]>;
  adminCompanies: PortfolioCompany[];
  portfolios: Portfolio[];
  isCustomRole: boolean;
};

export class EditSingleShareholderController {
  orgNumber: string;
  shareholderDetails?: ShareholderDetailsResponseDTO;
  storeShareholderResult?: Result;
  shareholderId?: number;
  model?: EditSingleShareholderFormModel;
  apiService: ApiService;
  messageStore: AlertMessageStore;
  isShareholderView?: boolean;
  onSuccess: (shareholder?: ExtendedShareholderDTO) => void;
  onClose: () => void;
  loadShareholders: () => Promise<Actor[]>;
  shareholders: Actor[] = [];
  locale: Language;
  isLoading = false;
  isLoadingShareholders = false;
  selectedTab: EditSingleShareholderTabs = EditSingleShareholderTabs.ALL_FIELD;

  allPortfolios: Portfolio[];
  allAdminCompanies: PortfolioCompany[];
  companySelectorContext: RadioFieldModel<CompanySelectorContextOption | null>;
  companies: OptionFieldModel<PortfolioCompany>;
  isCustomRole: boolean;

  constructor(params: EditSingleShareholderParams) {
    this.orgNumber = params.orgNumber;
    this.shareholderId = params.shareholderId;
    this.apiService = params.apiService;
    this.messageStore = params.messageStore;
    this.isShareholderView = params.isShareholderView;
    this.onSuccess = params.onSuccess;
    this.onClose = params.onClose;
    this.locale = params.locale;
    this.loadShareholders = params.loadShareholders;

    this.allPortfolios = params.portfolios;
    this.allAdminCompanies = params.adminCompanies;
    this.companySelectorContext = new RadioFieldModel({
      value: this.selectedCompanySelectorContextOption(this.orgNumber),
      options: () => [...this.companySelectorContextOptions(this.orgNumber), null],
      labelFn: (item) =>
        item ? { translated: item.name } : { key: "shareholders.exportContent.show-all" },
      idFn: (item) => item?.id ?? "all",
      iconFn: (item) => item?.icon ?? "globe",
      onChange: () => this.companies.toggleAll(true),
    });
    this.companies = new OptionFieldModel({
      options: () => this.filteredCompanies,
      selectedIds: [this.orgNumber],
      idFn: (item) => item.organisationNumber,
      labelFn: (item) => ({ translated: item.name }),
    });
    this.isCustomRole = params.isCustomRole;

    makeAutoObservable(this, {}, { autoBind: true });
  }

  get numberOfSelectedCompanies() {
    return this.companies.selected.length;
  }

  selectedCompanySelectorContextOption(selectedContext?: string) {
    return (
      this.companySelectorContextOptions(selectedContext).find((p) => p.id === selectedContext) ??
      null
    );
  }

  companySelectorContextOptions(selectedContext?: string): CompanySelectorContextOption[] {
    const companyContext = this.allAdminCompanies.find(
      (c) => c.organisationNumber === selectedContext,
    );
    return prepCompanySelectorContextOptions(this.portfoliosWithCompanies, companyContext);
  }

  get portfoliosWithCompanies() {
    return this.allPortfolios.filter((portfolio) =>
      this.allAccessibleCompanies.some((company) => company.portfolioId === portfolio.id),
    );
  }

  selectTab(tab: EditSingleShareholderTabs) {
    this.selectedTab = tab;
    this.model?.filterFields(tab === EditSingleShareholderTabs.ALL_FIELD);
  }

  get showEditSingleShareholderForm() {
    return this.selectedTab !== EditSingleShareholderTabs.SELECT_COMPANIES;
  }

  get filteredCompanies() {
    const selectedCompanySelectorContext = this.companySelectorContext.value;
    return selectedCompanySelectorContext
      ? this.allAccessibleCompanies.filter(
          (company) =>
            (selectedCompanySelectorContext.type === CompanySelectorContextType.PORTFOLIO &&
              selectedCompanySelectorContext.id === company.portfolioId) ||
            (selectedCompanySelectorContext.type === CompanySelectorContextType.COMPANY &&
              selectedCompanySelectorContext.id === company.organisationNumber),
        )
      : this.allAccessibleCompanies;
  }

  get isBatchUpdate() {
    return this.allAccessibleCompanies.length > 1 && !this.isCustomRole && !this.isShareholderView;
  }

  get allAccessibleCompanies() {
    return this.allAdminCompanies.filter(
      (company) =>
        company.organisationNumber === this.orgNumber ||
        this.allAccessibleShareholderDetails.some(
          (details) => details.organisationNumber === company.organisationNumber,
        ),
    );
  }

  get allAccessibleShareholderDetails() {
    return this.shareholderDetails?.accessibleShareholderDetails ?? [];
  }

  get accessibleShareholderDetails() {
    return (
      this.allAccessibleShareholderDetails.filter((d) => d.shareholderId !== this.shareholderId) ??
      []
    );
  }

  get allSelectedShareholderDetails() {
    return this.allAccessibleShareholderDetails.filter((details) =>
      this.companies.selected.some(
        (company) => company.organisationNumber === details.organisationNumber,
      ),
    );
  }

  get showCopyFrom() {
    return this.accessibleShareholderDetails.length > 0;
  }

  get shareholderOrCompanyName() {
    return this.shareholderDetails?.isCompany
      ? (this.shareholderDetails?.companyName ?? "")
      : this.shareholderName;
  }

  get shareholderName() {
    return this.shareholderDetails?.personInfo
      ? formatName(this.shareholderDetails.personInfo)
      : "";
  }

  setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  setLoadingShareholders(isLoading: boolean) {
    this.isLoadingShareholders = isLoading;
  }

  initEmptyShareholder() {
    this.model = new EditSingleShareholderFormModel({
      orgNumber: this.orgNumber,
      locale: this.locale,
      isDuplicateShareholder: this.isDuplicateShareholder,
      isShareholderView: this.isShareholderView,
    });
  }

  *getShareholderDetails(shareholderId: number, isCopyFromAccessableShareholder: boolean = false) {
    try {
      this.setLoading(true);
      const shareholderDetails: ShareholderDetailsResponseDTO =
        yield this.apiService.fetchShareholderById(shareholderId);
      this.model = new EditSingleShareholderFormModel({
        orgNumber: this.orgNumber,
        locale: this.locale,
        isDuplicateShareholder: this.isDuplicateShareholder,
        isShareholderView: this.isShareholderView,
        shareholder: {
          ...shareholderDetails,
          utlNumber: isCopyFromAccessableShareholder
            ? this.model?.utlNumber.validatedValue
            : shareholderDetails.utlNumber,
        },
      });
      if (this.shareholderDetails === undefined) {
        this.shareholderDetails = shareholderDetails;
      }
    } catch (e) {
      this.messageStore.showError({ key: "shareholders.error.load" });
    } finally {
      this.setLoading(false);
    }
  }

  get existingShareholders() {
    return this.shareholders.filter((actor) =>
      actor.availableActors.some((available) => available.organisationNumber === this.orgNumber),
    );
  }

  get existingShareholderExcludingCurrent() {
    return this.shareholderId !== undefined
      ? this.existingShareholders.filter((actor) =>
          actor.availableActors.some(
            (available) =>
              this.shareholderId !== undefined &&
              available.reference !== toShareholderReference(this.shareholderId),
          ),
        )
      : this.existingShareholders;
  }

  isDuplicateShareholder(isCompany: boolean, value: string) {
    return isCompany
      ? this.isDuplicateCompanyShareholder(value)
      : this.isDuplicatePersonShareholder(value);
  }

  isDuplicateCompanyShareholder(companyNumber: string) {
    return (
      this.model?.isCompany.value === true &&
      this.existingShareholderExcludingCurrent.length > 0 &&
      this.existingShareholderExcludingCurrent.some(
        (shareholder) => shareholder.companyNumber === companyNumber,
      )
    );
  }

  isDuplicatePersonShareholder(socialSecurityNumber: string) {
    return (
      this.model?.isCompany.value === false &&
      this.existingShareholderExcludingCurrent.length > 0 &&
      this.existingShareholderExcludingCurrent.some(
        (shareholder) => shareholder.socialSecurityNumber === socialSecurityNumber,
      )
    );
  }

  *fetchShareholders() {
    if (this.shareholders.length === 0) {
      this.setLoadingShareholders(true);
      this.shareholders = yield this.loadShareholders();
      this.setLoadingShareholders(false);
      this.model?.companyNumber.validate();
      this.model?.personInfoModel?.socialSecurityNumber.validate();
    }
  }

  *saveShareholder() {
    try {
      const selectedShareholderIds = this.isBatchUpdate
        ? this.allSelectedShareholderDetails.map((d) => d.shareholderId)
        : [];

      if (this.model !== undefined && this.model?.validate()) {
        this.storeShareholderResult = pending();
        const result: ExtendedShareholderDTO | undefined = yield this.insertOrUpdateShareholder(
          this.model.toDTO(selectedShareholderIds),
          this.isShareholderView,
          this.shareholderId,
        );
        this.onSuccess(result);
        this.onClose();
        this.storeShareholderResult = ok();
      }
    } catch (e) {
      this.messageStore.showError({ key: "shareholders.editSingle.error" });
      this.storeShareholderResult = failure();
    }
  }

  insertOrUpdateShareholder(
    shareholderDTO: UpdateShareholderDTO,
    isShareholderView: boolean = false,
    shareholderId?: number,
  ): Promise<ExtendedShareholderDTO> | undefined {
    return shareholderId
      ? this.updateShareholder(shareholderId, shareholderDTO, isShareholderView)
      : this.insertShareholder(shareholderDTO);
  }

  updateShareholder(
    shareholderId: number,
    shareholderDTO: UpdateShareholderDTO,
    isShareholderView: boolean = false,
  ) {
    return isShareholderView
      ? this.apiService.updateShareholderInfo(shareholderId, shareholderDTO)
      : this.apiService.updateShareholder(shareholderId, shareholderDTO);
  }

  insertShareholder(
    shareholderDTO: UpdateShareholderDTO,
    isShareholderView: boolean = false,
  ): Promise<ExtendedShareholderDTO> | undefined {
    return isShareholderView ? undefined : this.apiService.insertShareholder(shareholderDTO);
  }
}

export function copyfromAccessibleShareholdersDropdown(
  shareholders: AccessibleShareholderDetail[],
  getShareholderDetails: (shareholderId: number, isCopyFromAccessableShareholder: boolean) => void,
): DropdownMenuProps {
  return {
    button: {
      type: "link",
      label: { key: "edit-shareholder-dialog.copy-from" },
    },
    items: shareholders.map((shareholder) => {
      return {
        type: "item",
        icon: "buildings",
        label: { translated: shareholder.companyName },
        onSelect: () => getShareholderDetails(shareholder.shareholderId, true),
      };
    }),
  };
}

export function editSingleShareholderTabs(
  controller: EditSingleShareholderController,
  model: EditSingleShareholderFormModel,
): TabsProps {
  return {
    scrollable: false,
    tabs: compact([
      {
        id: EditSingleShareholderTabs.ALL_FIELD,
        label: { key: "shareholders.editSingle.filter.allFields" },
        isActive: EditSingleShareholderTabs.ALL_FIELD === controller.selectedTab,
        onSelect: () => controller.selectTab(EditSingleShareholderTabs.ALL_FIELD),
      },
      {
        id: EditSingleShareholderTabs.NOT_SET,
        label: { key: "shareholders.editSingle.filter.notSet" },
        tag: {
          label: model.numberOfEmptyFields.toString(),
          color: model.numberOfEmptyFields > 0 ? "info" : "success",
        },
        isActive: EditSingleShareholderTabs.NOT_SET === controller.selectedTab,
        onSelect: () => controller.selectTab(EditSingleShareholderTabs.NOT_SET),
      },
      controller.isBatchUpdate && {
        id: EditSingleShareholderTabs.SELECT_COMPANIES,
        label: {
          key: "shareholders.editSingle.filter.selectCompanies",
          params: { n: controller.numberOfSelectedCompanies },
        },
        isActive: EditSingleShareholderTabs.SELECT_COMPANIES === controller.selectedTab,
        onSelect: () => controller.selectTab(EditSingleShareholderTabs.SELECT_COMPANIES),
      },
    ]),
  };
}

export class EditSingleShareholderFormModel {
  data?: ShareholderDetailsResponseDTO;
  personInfoModel?: EditPersonInfoFormModel;
  contactInfo?: EditPersonInfoFormModel;
  companyName: StringFieldModel;
  companyNumber: StringFieldModel;
  shareholderAddress: EditAddressFormModel;
  bic: StringFieldModel;
  iban: StringFieldModel;
  bankDetailsCountry: StringFieldModel;
  utlNumber: StringFieldModel;
  nationality: RadioFieldModel<{
    code: string;
    label: { translated: string };
  }>;
  issuedShareholderCertificate: BooleanFieldModel;

  organizationNumber: string;
  isCompany: RadioFieldModel<boolean>;
  isEmployee: RadioFieldModel<boolean | null>;

  showAll = true;
  showEmptyFields: string[] = [];
  isDuplicateShareholder?: (isCompany: boolean, value: string) => boolean;
  isShareholderView?: boolean;

  constructor({
    orgNumber,
    locale,
    isDuplicateShareholder,
    isShareholderView,
    shareholder,
  }: {
    orgNumber: string;
    locale: Language;
    isDuplicateShareholder?: (isCompany: boolean, value: string) => boolean;
    isShareholderView?: boolean;
    shareholder?: ShareholderDetailsResponseDTO;
  }) {
    this.isDuplicateShareholder = isDuplicateShareholder;
    this.isShareholderView = isShareholderView;
    this.data = shareholder;
    this.organizationNumber = orgNumber;
    this.isCompany = new RadioFieldModel<boolean>({
      required: true,
      value: shareholder?.isCompany ?? false,
      options: [true, false],
      idFn: (v) => String(v),
      labelFn: (option) => ({
        key: option
          ? "shareholders.editSingle.isCompany.true"
          : "shareholders.editSingle.isCompany.false",
      }),
      onChange: (value) => this.onIsCompanyChange(value),
    });
    if (this.isCompany.value) {
      this.contactInfo = new EditPersonInfoFormModel(
        shareholder?.contactInfo ?? emptyPersonInfo,
        this.isCompany.value,
        isDuplicateShareholder,
        isShareholderView,
      );
    } else {
      this.personInfoModel = new EditPersonInfoFormModel(
        shareholder?.personInfo ?? emptyPersonInfo,
        this.isCompany.value,
        isDuplicateShareholder,
        isShareholderView,
      );
    }
    this.companyName = new StringFieldModel({
      value: shareholder?.companyName,
      required: this.isCompany.value,
    });
    this.companyNumber = new StringFieldModel({
      value: shareholder?.companyNumber,
      validator: (value) => {
        const inputValue = removeAllSpaces(value);
        this.companyNumber.setInputValue(inputValue);
        if (isDuplicateShareholder?.(this.isCompany.value, inputValue)) {
          return { key: "nonUniqueCompanyNumber" };
        } else if (this.nationality.value.code === norway && inputValue.length !== 9) {
          return { key: "invalidNorwegianOrgNo" };
        }
      },
    });
    this.shareholderAddress = new EditAddressFormModel(shareholder?.shareholderAddress, locale);
    this.bic = new StringFieldModel({ value: shareholder?.bic });
    this.iban = new StringFieldModel({ value: shareholder?.iban });
    this.bankDetailsCountry = new StringFieldModel({
      value: shareholder?.bankDetailsCountry,
    });
    this.utlNumber = new StringFieldModel({
      value: shareholder?.utlNumber,
      validator: utlNumberValidator,
    });

    this.nationality = makeCountryRadioFieldModel(locale, shareholder?.nationality);
    this.issuedShareholderCertificate = new BooleanFieldModel({
      value: shareholder?.issuedShareholderCertificate,
    });

    this.isEmployee = new RadioFieldModel<boolean | null>({
      value: shareholder?.isEmployee === undefined ? null : shareholder.isEmployee,
      options: [null, true, false],
      idFn: (value) => String(value),
      labelFn: (value) => getBooleanLabel(value),
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }

  selectCompany(company?: SuggestedCompany) {
    if (company) {
      this.companyName.setAndCommitInputValue(company.name);
      this.companyNumber.setAndCommitInputValue(company.organizationnumber);
    } else {
      this.companyName.clear();
      this.companyNumber.clear();
    }
  }

  get selectedCompany(): SuggestedCompany {
    return { name: this.companyName.inputValue, organizationnumber: this.companyNumber.inputValue };
  }

  filterFields(value: boolean) {
    this.showAll = value;
    this.showEmptyFields = this.emptyFields;
  }

  get emptyFields(): string[] {
    const personInfoFields = this.isCompany.value
      ? this.contactInfo?.findEmptyFields
      : this.personInfoModel?.findEmptyFields;
    return compact([
      !this.isShareholderView && this.isCompany.value && this.companyName.isEmpty && "companyName",
      !this.isShareholderView &&
        this.isCompany.value &&
        this.companyNumber.isEmpty &&
        "companyNumber",
      ...this.shareholderAddress.findEmptyFields(),
      ...(personInfoFields ?? []),
      this.bic.isEmpty && "bic",
      this.iban.isEmpty && "iban",
      this.bankDetailsCountry.isEmpty && "bankDetailsCountry",
      !this.isShareholderView && this.utlNumber.isEmpty && "utlNumber",
      this.isEmployee.value === null && "isEmployee",
    ]);
  }

  get numberOfEmptyFields() {
    return this.emptyFields.length;
  }

  showField(field: string) {
    return this.showAll || this.showEmptyFields.includes(field);
  }

  get showCompanyDetails() {
    return (
      this.showField("nationality") ||
      this.showField("companyNumber") ||
      this.showField("utlNumber") ||
      this.showField("companyName") ||
      this.showField("address") ||
      this.showField("postalCode") ||
      this.showField("city") ||
      this.showField("country")
    );
  }

  get showPersonInfo() {
    return (
      this.showField("socialSecurityNumber") ||
      this.showField("firstName") ||
      this.showField("middleName") ||
      this.showField("lastName") ||
      this.showField("email") ||
      this.showField("birthDate") ||
      this.showField("phoneNumber") ||
      this.showField("address") ||
      this.showField("postalCode") ||
      this.showField("city") ||
      this.showField("gender") ||
      this.showField("utlNumber")
    );
  }

  get showBankDetails() {
    return this.showField("bic") || this.showField("iban") || this.showField("bankDetailsCountry");
  }

  onIsCompanyChange(isCompany: boolean) {
    if (isCompany) {
      this.companyName.setRequired(true);
      this.contactInfo = new EditPersonInfoFormModel(
        this.personInfoModel?.toDTO() ?? emptyPersonInfo,
        isCompany,
        this.isDuplicateShareholder,
        this.isShareholderView,
      );
    } else {
      this.companyName.setRequired(false);
      this.companyName.validate();
      this.personInfoModel = new EditPersonInfoFormModel(
        this.contactInfo?.toDTO() ?? emptyPersonInfo,
        isCompany,
        this.isDuplicateShareholder,
        this.isShareholderView,
      );
    }
  }

  toDTO(shareholderIds: number[]): UpdateShareholderDTO {
    return {
      shareholderIds: shareholderIds,
      organizationnumber: this.organizationNumber,
      personInfo: this.isCompany.value ? undefined : this.personInfoModel?.toDTO(),
      contactInfo: this.isCompany.value ? this.contactInfo?.toDTO() : undefined,
      companyName: this.isCompany.value ? unblank(this.companyName.validatedValue) : undefined,
      companyNumber: this.isCompany.value
        ? unblank(removeAllSpaces(this.companyNumber.validatedValue))
        : undefined,
      shareholderAddress: this.shareholderAddress?.toDTO(),
      isCompany: this.isCompany.value,
      bic: unblank(this.bic.validatedValue),
      iban: unblank(this.iban.validatedValue),
      bankDetailsCountry: unblank(this.bankDetailsCountry.validatedValue),
      utlNumber: unblank(this.utlNumber.validatedValue),
      nationality: this.nationality.value.code,
      isEmployee: this.isEmployee.value === null ? undefined : this.isEmployee.value,
      issuedShareholderCertificate: this.issuedShareholderCertificate.value ?? false,
    };
  }

  validate() {
    this.personInfoModel?.validate(this.nationality.value.code);
    this.contactInfo?.validate(this.nationality.value.code);
    this.companyName.validate();
    this.companyNumber.validate();
    this.bic.validate();
    this.iban.validate();
    this.bankDetailsCountry.validate();
    this.utlNumber.validate();
    return this.isValid;
  }

  get isValid() {
    return (
      (this.isCompany.value ? this.contactInfo?.isValid : this.personInfoModel?.isValid) &&
      this.companyName.isValid &&
      this.companyNumber.isValid &&
      this.bic.isValid &&
      this.iban.isValid &&
      this.bankDetailsCountry.isValid &&
      this.utlNumber.isValid
    );
  }

  get isChanged() {
    return (
      (this.isCompany ? this.contactInfo?.isChanged : this.personInfoModel?.isChanged) ||
      this.companyName.isChanged ||
      this.companyNumber.isChanged ||
      this.shareholderAddress.isChanged ||
      this.bic.isChanged ||
      this.iban.isChanged ||
      this.bankDetailsCountry.isChanged ||
      this.utlNumber.isChanged ||
      this.nationality.isChanged
    );
  }
}

export class EditPersonInfoFormModel {
  personInfoId?: number;
  firstName: StringFieldModel;
  middleName: StringFieldModel;
  lastName: StringFieldModel;
  email: StringFieldModel;
  birthDate: LocalDateFieldModel;
  phoneNumber: PhoneInputModel;
  socialSecurityNumber: StringFieldModel;
  gender: RadioFieldModel<Gender | null>;

  constructor(
    personInfo: PersonInfo,
    isCompany: boolean,
    isDuplicateShareholder?: (isCompany: boolean, value: string) => boolean,
    readonly isShareholderView?: boolean,
  ) {
    this.personInfoId = personInfo.personInfoId;
    this.firstName = new StringFieldModel({
      value: personInfo.firstName,
      required: !isCompany && !isShareholderView,
    });
    this.middleName = new StringFieldModel({ value: personInfo.middleName });
    this.lastName = new StringFieldModel({
      value: personInfo.lastName,
      required: !isCompany && !isShareholderView,
    });
    this.email = createEmailField({ value: personInfo.email, required: false });
    this.birthDate = new LocalDateFieldModel({ value: personInfo.birthDate });
    this.phoneNumber = new PhoneInputModel({ value: personInfo.phoneNumber });
    this.socialSecurityNumber = new StringFieldModel({
      value: personInfo.socialSecurityNumber,
      validator: (value) => {
        if (!isCompany && isDuplicateShareholder?.(isCompany, value)) {
          return { key: "nonUniqueSocialSecurityNumber" };
        }
      },
    });
    this.gender = new RadioFieldModel<Gender | null>({
      value: personInfo.gender || null,
      options: [null, "male", "female"],
      idFn: (value) => String(value),
      labelFn: (value) => getGenderLabel(value),
    });
  }

  get findEmptyFields(): string[] {
    return compact([
      !this.isShareholderView && this.firstName.isEmpty && "firstName",
      !this.isShareholderView && this.middleName.isEmpty && "middleName",
      !this.isShareholderView && this.lastName.isEmpty && "lastName",
      this.email.isEmpty && "email",
      this.birthDate.isEmpty && "birthDate",
      this.phoneNumber.isEmpty && "phoneNumber",
      !this.isShareholderView && this.socialSecurityNumber.isEmpty && "socialSecurityNumber",
      this.gender.value === null && "gender",
    ]);
  }

  toDTO(): PersonInfo {
    return {
      personInfoId: this.personInfoId,
      firstName: this.firstName.validatedValue,
      middleName: this.middleName.validatedValue,
      lastName: this.lastName.validatedValue,
      email: this.email.validatedValue ? this.email.validatedValue : undefined,
      birthDate: this.birthDate.isEmpty ? undefined : this.birthDate.validatedValue,
      phoneNumber: this.phoneNumber.isEmpty ? undefined : this.phoneNumber.toValidPhoneNumber(),
      socialSecurityNumber: unblank(this.socialSecurityNumber.validatedValue),
      gender: this.gender.value || undefined,
    };
  }

  validate(countryCode: string) {
    this.firstName.validate();
    this.middleName.validate();
    this.lastName.validate();
    this.email.validate();
    this.birthDate.validate();
    this.phoneNumber.validate();
    this.socialSecurityNumber.validate();
    if (this.socialSecurityNumber.validatedValue) {
      if (countryCode === "NO") {
        const result = idnr(this.socialSecurityNumber.validatedValue);
        if (result.status === "invalid") {
          this.socialSecurityNumber.setError({ key: "invalidSocialSecurityNumber" });
        }
      } else if (countryCode === "SE") {
        if (!isValidSwedishNationalId(this.socialSecurityNumber.validatedValue, countryCode)) {
          this.socialSecurityNumber.setError({ key: "invalidSocialSecurityNumber" });
        }
      }
    }
  }

  get isValid() {
    return (
      this.firstName.isValid &&
      this.middleName.isValid &&
      this.lastName.isValid &&
      this.email.isValid &&
      this.birthDate.isValid &&
      this.phoneNumber.isValid &&
      this.socialSecurityNumber.isValid
    );
  }

  get isChanged() {
    return (
      this.firstName.isChanged ||
      this.middleName.isChanged ||
      this.lastName.isChanged ||
      this.email.isChanged ||
      this.birthDate.isChanged ||
      this.phoneNumber.isChanged ||
      this.socialSecurityNumber.isChanged
    );
  }
}

export function getBooleanLabel(value: boolean | null): TMessage {
  if (value === null) {
    return { translated: " " };
  } else if (value) {
    return { key: "yes" };
  } else {
    return { key: "no" };
  }
}

export class EditAddressFormModel {
  addressId?: number;
  address: StringFieldModel;
  postalCode: StringFieldModel;
  city: StringFieldModel;
  country: RadioFieldModel<{
    code: string;
    label: { translated: string };
  }>;

  constructor(address: AddressInfo | undefined, locale: Language) {
    this.addressId = address?.addressId;
    this.address = new StringFieldModel({ value: address?.address });
    this.city = new StringFieldModel({ value: address?.city });
    this.postalCode = new StringFieldModel({
      value: address?.postalCode,
      onChange: (value) => {
        if (value) {
          const city = lookupNorwegianCity(value);
          if (city) {
            this.city.setAndCommitInputValue(city);
          }
        }
      },
    });

    this.country = makeCountryRadioFieldModel(locale, address?.country);
  }

  findEmptyFields(): string[] {
    return compact([
      this.address.isEmpty && "address",
      this.postalCode.isEmpty && "postalCode",
      this.city.isEmpty && "city",
    ]);
  }

  toDTO(): AddressInfo {
    return {
      addressId: this.addressId,
      address: unblank(this.address.validatedValue),
      postalCode: unblank(this.postalCode.validatedValue),
      city: unblank(this.city.validatedValue),
      country: this.country.value.code,
    };
  }

  get isChanged() {
    return (
      this.address.isChanged ||
      this.postalCode.isChanged ||
      this.city.isChanged ||
      this.country.isChanged
    );
  }
}

function makeCountryRadioFieldModel(locale: Language, selectedCountry: string | undefined) {
  const countries = getAllCountriesSortedByName(locale);
  const countryOptions = countries.map((option) => ({
    code: option.code,
    label: { translated: option.name },
  }));
  const countryCode = selectedCountry ?? norway;
  return new RadioFieldModel<{
    code: string;
    label: { translated: string };
  }>({
    options: countryOptions,
    value: countryOptions.find((c) => c.code === countryCode) ?? countryOptions[0],
    idFn: (item) => item.code,
    labelFn: (item) => item.label,
  });
}

export function formatTextValue(value: string, maskLength: number | undefined) {
  if (maskLength === undefined || value.length <= maskLength) {
    return value;
  }
  return value.substring(0, maskLength) + "…";
}
